import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Layout from './components/Layout'
import CaptureExercise from './components/capture/CaptureExercise'
import CaptureSession from './components/capture/CaptureSession'
import ErrorBoundary from './components/ErrorBoundary'
import SystemIndicator from './components/SystemIndicator'
import SessionTimeoutModal from './components/SessionTimeoutModal'
import Dashboard from './pages/Dashboard'
import BloodTests from './pages/BloodTests'
import BloodTestDashboard from './pages/BloodTestDashboard'
import TrendAnalysis from './pages/TrendAnalysis'
import ComparisonView from './pages/ComparisonView'
import NarrativeReport from './pages/NarrativeReport'
import Supplements from './pages/Supplements'
import DailySupplementReport from './pages/DailySupplementReport'
import SupplementHistoryReport from './pages/SupplementHistoryReport'
import EquipmentManagement from './pages/EquipmentManagement'
import ExerciseLibrary from './pages/ExerciseLibrary'
import TrainingLocations from './pages/TrainingLocations'
import TrainingLocationDetail from './pages/TrainingLocationDetail'
import WeeklyPrograms from './pages/WeeklyPrograms'
import TrainingTemplates from './pages/TrainingTemplates'
import LogSession from './pages/LogSession'
import Nutrition from './pages/Nutrition'
import Recipes from './pages/Recipes'
import Shopping from './pages/Shopping'
import RecipeDetail from './pages/RecipeDetail'
import RecipeCapture from './pages/RecipeCapture'
import Reports from './pages/Reports'
import CycleView from './components/health-cycle/CycleView'
import Profile from './pages/Profile'
import MyProgress from './pages/MyProgress'
import Login from './pages/Login'
import Security from './pages/Security'
import Admin from './pages/Admin'
import Help from './pages/Help'
import EatToday from './pages/EatToday'
import { useAuth } from './hooks/useAuth'

function App() {
  const { user, loading } = useAuth()
  const location = useLocation()

  // VH-113: Public capture route. This is an INTENTIONAL, user-approved
  // no-login capability page: the unguessable per-exercise capture_token in the
  // URL IS the authorization (see app/routers/capture.py). It must render
  // OUTSIDE the authenticated Layout and BEFORE the auth gate so an
  // unauthenticated visitor (arriving by scanning a printed QR) is never
  // bounced to /login and never sees the app chrome/nav.
  if (location.pathname.startsWith('/capture/')) {
    return (
      <>
        <Routes>
          {/* VH-114: whole-session capture. MUST be registered BEFORE
              /capture/:token, otherwise the literal "session" segment would be
              greedily matched as a per-exercise token. */}
          <Route
            path="/capture/session/:token"
            element={
              <ErrorBoundary>
                <CaptureSession />
              </ErrorBoundary>
            }
          />
          <Route
            path="/capture/:token"
            element={
              <ErrorBoundary>
                <CaptureExercise />
              </ErrorBoundary>
            }
          />
        </Routes>
        {/* VH-298: a signed-in user reporting a workout keeps the login alive
            here too — the page saves with its QR token, which the server does
            not count as use. A QR-only visitor has no login to keep. */}
        {user && <SessionTimeoutModal />}
        <SystemIndicator />
      </>
    )
  }

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
        <SystemIndicator />
      </>
    )
  }

  if (!user) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-up" element={<Login />} />
          <Route path="*" element={<Login />} />
        </Routes>
        <SystemIndicator />
      </>
    )
  }

  // VH-248: nutrition on a phone. Signed in, but outside the Layout — no sidebar, no bars,
  // nothing but today and the ways to log a meal.
  if (location.pathname === '/eat') {
    return (
      <>
        <ErrorBoundary><EatToday /></ErrorBoundary>
        {/* VH-298: outside the Layout, so the idle clock is mounted here. */}
        <SessionTimeoutModal />
        <SystemIndicator />
      </>
    )
  }

  return (
    <Layout>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/security" element={<ErrorBoundary><Security /></ErrorBoundary>} />
          <Route path="/blood-tests" element={<ErrorBoundary><BloodTests /></ErrorBoundary>} />
          <Route path="/blood-tests/compare" element={<ErrorBoundary><ComparisonView /></ErrorBoundary>} />
          <Route path="/blood-tests/narrative-report" element={<ErrorBoundary><NarrativeReport /></ErrorBoundary>} />
          <Route path="/blood-tests/:id" element={<ErrorBoundary><BloodTestDashboard /></ErrorBoundary>} />
          <Route path="/blood-tests/:id/trends" element={<ErrorBoundary><TrendAnalysis /></ErrorBoundary>} />
          <Route path="/supplements" element={<ErrorBoundary><Supplements /></ErrorBoundary>} />
          <Route path="/supplements/daily-report" element={<ErrorBoundary><DailySupplementReport /></ErrorBoundary>} />
          <Route path="/supplements/history-report" element={<ErrorBoundary><SupplementHistoryReport /></ErrorBoundary>} />
          <Route path="/workouts" element={<ErrorBoundary><WeeklyPrograms /></ErrorBoundary>} />
          <Route path="/equipment" element={<ErrorBoundary><EquipmentManagement /></ErrorBoundary>} />
          <Route path="/exercises" element={<ErrorBoundary><ExerciseLibrary /></ErrorBoundary>} />
          {/* VH-126: the place is the home screen of the scanning flow. */}
          <Route path="/training-locations" element={<ErrorBoundary><TrainingLocations /></ErrorBoundary>} />
          <Route path="/training-locations/:id" element={<ErrorBoundary><TrainingLocationDetail /></ErrorBoundary>} />
          <Route path="/templates" element={<ErrorBoundary><TrainingTemplates /></ErrorBoundary>} />
          {/* VH-138: authenticated entry to the capture sheet, for when the
              printed QR is not to hand. The sheet itself stays public. */}
          <Route path="/log-session" element={<ErrorBoundary><LogSession /></ErrorBoundary>} />
          <Route path="/nutrition" element={<ErrorBoundary><Nutrition /></ErrorBoundary>} />
          {/* VH-253: the shopping list is used standing in a shop — one short,
              typeable path, no nesting under /nutrition. */}
          <Route path="/shopping" element={<ErrorBoundary><Shopping /></ErrorBoundary>} />
          <Route path="/recipes" element={<ErrorBoundary><Recipes /></ErrorBoundary>} />
          <Route path="/recipes/new" element={<ErrorBoundary><RecipeCapture /></ErrorBoundary>} />
          <Route path="/recipes/:id" element={<ErrorBoundary><RecipeDetail /></ErrorBoundary>} />
          {/* VH-227: Health Report became Health Cycle; the old route redirects. */}
          <Route path="/health-cycle" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
          <Route path="/health-cycle/cycles/:id" element={<ErrorBoundary><CycleView /></ErrorBoundary>} />
          <Route path="/reports" element={<Navigate to="/health-cycle" replace />} />
          <Route path="/reports/cycles/:id" element={<ErrorBoundary><CycleView /></ErrorBoundary>} />
          <Route path="/my-progress" element={<ErrorBoundary><MyProgress /></ErrorBoundary>} />
          <Route path="/profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
          <Route path="/admin" element={<ErrorBoundary><Admin /></ErrorBoundary>} />
          <Route path="/help" element={<ErrorBoundary><Help /></ErrorBoundary>} />
          <Route path="/help/:section" element={<ErrorBoundary><Help /></ErrorBoundary>} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </ErrorBoundary>
    </Layout>
  )
}

export default App